import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../features/auth/authSlice";
import NavItem from "../common/NavItem";
import axios from "axios";
import toast from "react-hot-toast";

const logoUrl = new URL("../../../assets/Logo_Marco.png", import.meta.url).href;

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userRef = useRef(null);

  // Outside-Click für User-Menü + ESC für beide Menüs
  useEffect(() => {
    const onClickOutside = (event) => {
      if (userRef.current && !userRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  // Body-Scroll sperren, wenn Mobile-Menü offen
  useEffect(() => {
    if (menuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => (document.body.style.overflow = "");
  }, [menuOpen]);

  const handleLogout = async (e) => {
    e.stopPropagation();

    try {
      await axios.post(
        `${process.env.BACKEND_URL}/api/logout`,
        {},
        { withCredentials: true },
      );
      dispatch(logout());
      setUserMenuOpen(false);
      navigate("/login");
    } catch (error) {
      console.error("Logout fehlgeschlagen:", error);
      toast.error("Logout fehlgeschlagen. Bitte erneut versuchen.");
    }
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-white border-b border-knapp-border">
        {/* Volle Breite: Logo und Konto/Warenkorb haben denselben Abstand (px-6) zum Rand */}
        <div className="h-20 px-6">
          <div className="h-full flex items-center justify-between relative">
            {/* Logo */}
            <div className="flex-1 flex items-center">
              <Link to="/home" className="flex items-center">
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="max-w-xs h-12 md:h-18 lg:h-20 object-contain cursor-pointer"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center justify-center space-x-8 whitespace-nowrap text-xl 2xl:text-2xl font-medium h-full">
              <NavItem to="/home" label="Home" />
              <NavItem to="/produkte" label="Produkte" />
              <NavItem to="/brandschutz" label="Brandschutz" />
              <NavItem to="/feuerungskontrolle" label="Feuerungskontrollen" />
              <NavItem to="/service" label="Service Löschgeräte" />
              {user?.role === "admin" && <NavItem to="/admin" label="Admin" />}
            </nav>

            {/* Konto + Warenkorb + Mobile Toggle */}
            <div className="flex-1 flex items-center justify-end space-x-4 md:space-x-6 whitespace-nowrap text-base md:text-lg 2xl:text-xl font-medium">
              {/* User Menu */}
              <div className="relative" ref={userRef}>
                <button
                  aria-label="Benutzermenü"
                  className="rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-knapp-accent focus-visible:ring-offset-2"
                  aria-expanded={userMenuOpen}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!user) {
                      navigate("/login");
                      return;
                    }
                    setUserMenuOpen((prev) => !prev);
                  }}
                >
                  <span
                    className={`relative inline-block font-medium transition-all duration-200
                      after:content-[''] after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:bg-knapp-accent
                      after:transition-transform after:duration-300 after:origin-left
                      ${userMenuOpen ? "after:w-full" : "after:w-0 hover:after:w-full"}`}
                  >
                    Konto
                  </span>
                </button>

                {userMenuOpen && user && (
                  <div className="absolute right-0 mt-3 w-44 flex flex-col py-1 whitespace-normal bg-white border border-knapp-border shadow-lg rounded-[10px] z-[60] text-base font-normal overflow-hidden">
                    <button
                      onClick={() => {
                        navigate("/profile");
                        setUserMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-knapp-surface"
                    >
                      Profil
                    </button>
                    <button
                      onClick={() => {
                        navigate("/bestellungen");
                        setUserMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-knapp-surface"
                    >
                      Bestellungen
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 mt-1 border-t border-knapp-borderSubtle font-medium text-knapp-accent hover:bg-knapp-surface"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>

              {/* Cart */}
              <NavItem to="/warenkorb" label="Warenkorb" />

              {/* Mobile Menu Toggle */}
              <button
                className="xl:hidden p-2"
                aria-label="Menü umschalten"
                onClick={() => setMenuOpen((v) => !v)}
              >
                {menuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation (Overlay + Panel, unterhalb Headerhöhe 80px) */}
        {menuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/30 z-40 xl:hidden"
              onClick={() => setMenuOpen(false)}
            />
            {/* Panel */}
            <div className="fixed top-20 left-0 right-0 z-50 xl:hidden">
              <div className="mx-4 rounded-xl border border-knapp-border bg-white shadow-lg overflow-hidden">
                <nav className="flex flex-col items-stretch divide-y divide-knapp-borderSubtle text-lg font-medium">
                  <Link
                    to="/home"
                    onClick={() => setMenuOpen(false)}
                    className="px-5 py-3 hover:bg-knapp-surface"
                  >
                    Home
                  </Link>
                  <Link
                    to="/produkte"
                    onClick={() => setMenuOpen(false)}
                    className="px-5 py-3 hover:bg-knapp-surface"
                  >
                    Produkte
                  </Link>
                  <Link
                    to="/brandschutz"
                    onClick={() => setMenuOpen(false)}
                    className="px-5 py-3 hover:bg-knapp-surface"
                  >
                    Brandschutz
                  </Link>
                  <Link
                    to="/feuerungskontrolle"
                    onClick={() => setMenuOpen(false)}
                    className="px-5 py-3 hover:bg-knapp-surface"
                  >
                    Feuerungskontrollen
                  </Link>
                  <Link
                    to="/service"
                    onClick={() => setMenuOpen(false)}
                    className="px-5 py-3 hover:bg-knapp-surface"
                  >
                    Service
                  </Link>
                </nav>
              </div>
            </div>
          </>
        )}
      </header>
    </>
  );
}
